import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibChipChoice, KibChipChoices } from '@chewy/kib-chips-react'
import { KibTextarea } from '@chewy/kib-fields-new-react'
import classNames from 'classnames'

import { FeedbackType } from '~/components/common/RhapFeedbackButton'
import i18n from '~/locales/i18n'

import {
  NEGATIVE_FEEDBACK_REASON,
  type NegativeFeedbackReasonKey,
} from './agentChatFeedbackReasons'
import { AgentResponseButton } from './AgentResponseButton'
import { MessageBubble } from './MessageBubble'

import '@chewy/kib-chips-styles/dist/styles/kib-chip-choice.css'
import '@chewy/kib-chips-styles/dist/styles/kib-chip-choices.css'
import styles from './ChatMessage.module.scss'

const MAX_FEEDBACK_CHARS = 200

const REASON_KEYS: Array<{
  key: NegativeFeedbackReasonKey
  translationString: string
}> = [
  {
    key: NEGATIVE_FEEDBACK_REASON.NOT_WHAT_I_ASKED,
    translationString: i18n.t('Agent:FEEDBACK_REASON_NOT_WHAT_I_ASKED'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.INCORRECT_OR_INCOMPLETE,
    translationString: i18n.t('Agent:FEEDBACK_REASON_INCORRECT_OR_INCOMPLETE'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.HARD_TO_UNDERSTAND,
    translationString: i18n.t('Agent:FEEDBACK_REASON_HARD_TO_UNDERSTAND'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.NOT_HELPFUL_ENOUGH,
    translationString: i18n.t('Agent:FEEDBACK_REASON_NOT_HELPFUL_ENOUGH'),
  },
  {
    key: NEGATIVE_FEEDBACK_REASON.OTHER,
    translationString: i18n.t('Agent:FEEDBACK_REASON_OTHER'),
  },
]

export interface AgentFeedbackMessageProps {
  feedback: FeedbackType | null
  negativeReason: NegativeFeedbackReasonKey | null
  onCancel?: () => void
  onSendFeedback: (
    reasonKey: NegativeFeedbackReasonKey,
    feedbackText?: string,
  ) => void
  setNegativeReason: (reasonKey: NegativeFeedbackReasonKey | null) => void
}

export const AgentFeedbackMessage: FC<AgentFeedbackMessageProps> = ({
  feedback,
  negativeReason,
  onCancel,
  setNegativeReason,
  onSendFeedback,
}) => {
  const { t } = useTranslation(['Agent', 'Common'])
  const [customFeedbackText, setCustomFeedbackText] = useState('')
  const [showMoreTextarea, setShowMoreTextarea] = useState(false)
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false)

  const charsRemaining = Math.max(
    0,
    MAX_FEEDBACK_CHARS - customFeedbackText.length,
  )
  const charsOver = Math.max(0, customFeedbackText.length - MAX_FEEDBACK_CHARS)
  const isOverLimit = charsOver > 0

  const selectReasonAndOpenTextDetails = (
    selectedReasonKey: NegativeFeedbackReasonKey,
  ) => {
    setNegativeReason(selectedReasonKey)
    setShowMoreTextarea(true)
  }

  const handleSendFeedback = () => {
    const trimmed = customFeedbackText.slice(0, MAX_FEEDBACK_CHARS).trim()
    onSendFeedback(negativeReason || NEGATIVE_FEEDBACK_REASON.OTHER, trimmed)
    setHasSubmittedFeedback(true)
  }

  const clickCancel = () => {
    setShowMoreTextarea(false)
    setCustomFeedbackText('')
    setNegativeReason(null)
    onCancel?.()
  }

  const showAppreciationBubble =
    feedback === FeedbackType.NEGATIVE && hasSubmittedFeedback
  const showNegativeReasonUI =
    feedback === FeedbackType.NEGATIVE && !hasSubmittedFeedback

  if (feedback === null) {
    return null
  }

  if (feedback === FeedbackType.POSITIVE) {
    return (
      <div className={styles.feedbackFollowUpBubbles}>
        <MessageBubble sender="agent">
          <p className={styles.messageContent}>
            {t('Agent:FEEDBACK_THANKS_POSITIVE')}
          </p>
        </MessageBubble>
      </div>
    )
  }

  return (
    <div className={styles.feedbackFollowUpBubbles}>
      {!hasSubmittedFeedback && (
        <MessageBubble sender="agent">
          <p className={styles.messageContent}>
            {t('Agent:FEEDBACK_THANKS_NEGATIVE_PROMPT')}
          </p>
          {showNegativeReasonUI && (
            <>
              <KibChipChoices className={styles.feedbackReasonButtons}>
                {REASON_KEYS.map(({ key, translationString }) => (
                  <KibChipChoice
                    className={classNames({
                      [styles.nonSelectedChoiceChirp]: negativeReason !== key,
                    })}
                    key={key}
                    selectedValue={negativeReason === key ? key : undefined}
                    value={key}
                    onClick={() => selectReasonAndOpenTextDetails(key)}
                  >
                    {translationString}
                  </KibChipChoice>
                ))}
              </KibChipChoices>
              {showMoreTextarea && (
                <div className={styles.feedbackMoreSection}>
                  <KibTextarea
                    help={
                      isOverLimit
                        ? undefined
                        : t('Agent:FEEDBACK_CHARACTERS_REMAINING', {
                            charsRemaining,
                          })
                    }
                    helpPlacement="bottom"
                    invalid={isOverLimit}
                    label={t('Agent:FEEDBACK_SHARE_DETAILS')}
                    messagesContent={
                      isOverLimit
                        ? t('Agent:FEEDBACK_CHARACTERS_OVER_LIMIT', {
                            charsOver,
                          })
                        : undefined
                    }
                    rows={3}
                    value={customFeedbackText}
                    onChange={(e) => setCustomFeedbackText(e.target.value)}
                  />
                  <div className={styles.feedbackMoreSectionActions}>
                    <AgentResponseButton
                      disabled={isOverLimit}
                      emphasis="primary"
                      theme="action"
                      onClick={handleSendFeedback}
                    >
                      {t('Common:SUBMIT_ACTION')}
                    </AgentResponseButton>
                    <AgentResponseButton
                      emphasis="tertiary"
                      theme="action"
                      onClick={clickCancel}
                    >
                      {t('Common:CANCEL_ACTION')}
                    </AgentResponseButton>
                  </div>
                </div>
              )}
            </>
          )}
        </MessageBubble>
      )}

      {showAppreciationBubble && negativeReason && (
        <MessageBubble sender="agent">
          <p
            className={`${styles.messageContent} ${styles.feedbackSubmittedText}`}
          >
            {t('Agent:FEEDBACK_APPRECIATION')}
          </p>
          <p className={styles.messageContent}>
            <strong>
              {REASON_KEYS.find((r) => r.key === negativeReason)
                ?.translationString ?? negativeReason}
            </strong>
            {customFeedbackText.trim() !== ''
              ? `: ${customFeedbackText.trim()}`
              : ''}
          </p>
        </MessageBubble>
      )}
    </div>
  )
}

export default AgentFeedbackMessage
